/**
 * Permission vocabulary for the Inventory Suite — see `rbac spec.md` and the
 * catalogues PERMISSIONS_INVENTORY.md / PERMISSIONS_MATERIAL.md / PERMISSIONS_PHARMACY.md.
 *
 * The access matrix itself lives in the BACKEND. This file only holds the code
 * strings the frontend asks about, plus the helpers that narrow status-driven
 * action sets by permission. Granted codes arrive from get_logged_in_user_info/
 * and are resolved in hooks/useInvRole.js.
 *
 * Fail-closed: a code that is not in the granted set is denied. There is no
 * static fallback — a role the backend has not seeded gets nothing.
 *
 * Frontend gating is not security (spec §6.5) — the backend must enforce the
 * same rules on approve / authorize / post-to-stock endpoints.
 */

export const INV_SUITE_BASE = '/inventory-suite';

/**
 * NOT the RBAC policy any more — permissions come from the backend.
 * These three exist solely to support utils/grnEntity.js canOverrideGrnPricing(),
 * which is a plain function called from per-line validation and so cannot use the
 * hook. Delete them together with that function once `grn overridepricing` is
 * seeded as a real permission.
 */
export const INV_ROLES = {
  STORE_MANAGER: 'store_manager',
  ADMIN: 'admin',
};

/**
 * Every gated action. Values are the backend permission codes verbatim —
 * lowercase `resource verb`, matching PERMISSIONS_INVENTORY.md / PERMISSIONS_MATERIAL.md.
 * They are only ever compared as strings.
 */
export const INV_ACTIONS = {
  /** Module overviews are separate permissions — one per workspace. */
  STOCK_OVERVIEW_VIEW: 'stockoverview view',
  MATERIAL_OVERVIEW_VIEW: 'materialoverview view',
  PHARMACY_OVERVIEW_VIEW: 'pharmacyoverview view',

  // ── Inventory Management (spec §3.1)
  QUEUES_VIEW: 'queues view',
  QUEUES_ISSUE: 'queues issue',
  QUEUES_ACKNOWLEDGE: 'queues acknowledge',

  REQUISITION_VIEW: 'requisition view',
  REQUISITION_CREATE: 'requisition create',
  REQUISITION_EDIT: 'requisition edit',
  REQUISITION_SUBMIT: 'requisition submit',
  REQUISITION_CANCEL: 'requisition cancel',
  REQUISITION_APPROVE: 'requisition approve',
  REQUISITION_REJECT: 'requisition reject',
  REQUISITION_RELEASE: 'requisition release',

  CENTRAL_ISSUE_CREATE: 'centralissue create',

  ISSUE_VIEW: 'issue view',
  ISSUE_CONFIRM: 'issue confirm',
  ISSUE_ACKNOWLEDGE: 'issue acknowledge',

  RETURN_VIEW: 'return view',
  RETURN_CREATE: 'return create',
  RETURN_CONFIRM: 'return confirm',
  RETURN_ACKNOWLEDGE: 'return acknowledge',
  RETURN_REJECT: 'return reject',

  CONSUMPTION_VIEW: 'consumption view',
  CONSUMPTION_CREATE: 'consumption create',

  CONSOLIDATION_VIEW: 'consolidation view',
  CONSOLIDATION_CREATE: 'consolidation create',

  STORE_VIEW: 'store view',
  STORE_CREATE: 'store create',
  STORE_DEACTIVATE: 'store deactivate',

  BALANCE_VIEW: 'balance view',

  OPENING_STOCK_VIEW: 'openingstock view',
  OPENING_STOCK_CREATE: 'openingstock create',

  TRANSFER_CREATE: 'transfer create',

  ADJUSTMENT_VIEW: 'adjustment view',
  ADJUSTMENT_CREATE: 'adjustment create',
  ADJUSTMENT_AUTHORIZE: 'adjustment authorize',
  ADJUSTMENT_CANCEL: 'adjustment cancel',

  DISCARD_VIEW: 'discard view',
  DISCARD_CREATE: 'discard create',
  DISCARD_EDIT: 'discard edit',
  DISCARD_APPROVE: 'discard approve',
  DISCARD_REJECT: 'discard reject',
  DISCARD_CANCEL: 'discard cancel',

  LEDGER_VIEW: 'ledger view',
  BATCH_TRACE_VIEW: 'batchtrace view',

  // ── Material Management (spec §3.2)
  /** Material groups, categories, item classes, UOMs, tax masters — one action, they move together. */
  MASTER_MANAGE: 'master manage',
  /** Item Master, all 6 tabs (no per-tab gating this cycle). */
  ITEM_MANAGE: 'item manage',

  VENDOR_VIEW: 'vendor view',
  VENDOR_CREATE: 'vendor create',
  VENDOR_EDIT: 'vendor edit',
  VENDOR_SUBMIT: 'vendor submit',
  VENDOR_APPROVE: 'vendor approve',
  VENDOR_REJECT: 'vendor reject',
  VENDOR_SUSPEND: 'vendor suspend',
  VENDOR_BLACKLIST: 'vendor blacklist',
  VENDOR_REACTIVATE: 'vendor reactivate',

  RFQ_VIEW: 'rfq view',
  RFQ_CREATE: 'rfq create',
  RFQ_EDIT: 'rfq edit',
  RFQ_PUBLISH: 'rfq publish',
  RFQ_CANCEL: 'rfq cancel',
  RFQ_CLOSE: 'rfq close',
  RFQ_AWARD: 'rfq award',

  WO_VIEW: 'workorder view',
  WO_CREATE: 'workorder create',
  WO_EDIT: 'workorder edit',
  WO_PUBLISH: 'workorder publish',
  WO_AWARD: 'workorder award',
  WO_CANCEL: 'workorder cancel',
  WO_EXECUTE: 'workorder execute',

  PO_VIEW: 'po view',
  PO_CREATE: 'po create',
  PO_EDIT: 'po edit',
  PO_SUBMIT: 'po submit',
  PO_DELETE: 'po delete',
  PO_APPROVE: 'po approve',
  PO_REJECT: 'po reject',
  PO_SEND: 'po send',
  PO_CLOSE: 'po close',
  PO_SHORT_CLOSE: 'po shortclose',
  PO_CANCEL: 'po cancel',

  GRN_VIEW: 'grn view',
  GRN_CREATE: 'grn create',
  /** Wizard steps 1–4: receipt lines + per-line accept/reject, persisted by complete-qc. */
  GRN_QC_ENTRY: 'grn qcentry',
  /** Wizard step 6 — the confirmation gate (spec §4). Manager only. */
  GRN_POST_TO_STOCK: 'grn posttostock',
  GRN_CREATE_VENDOR_RETURN: 'grn createvendorreturn',
  GRN_CREATE_INVOICE: 'grn createinvoice',

  REJECTED_VIEW: 'rejected view',
  REPLACEMENT_GRN_CREATE: 'replacementgrn create',

  VENDOR_RETURN_VIEW: 'vendorreturn view',
  VENDOR_RETURN_CREATE: 'vendorreturn create',
  VENDOR_RETURN_CONFIRM: 'vendorreturn confirm',
  VENDOR_RETURN_CANCEL: 'vendorreturn cancel',

  PURCHASE_INVOICE_VIEW: 'purchaseinvoice view',
  PURCHASE_INVOICE_CREATE: 'purchaseinvoice create',

  // ── Pharmacy Management (see PERMISSIONS_PHARMACY.md)
  DISPENSE_START: 'dispense start',
  DIRECTDISPENSE_WALKIN: 'directdispense walkin',
  DIRECTDISPENSE_QUICKREGISTER: 'directdispense quickregister',
  CONTROLLED_OVERRIDE: 'controlled override',
  REFUND_APPROVE: 'refund approve',
  REFUND_REJECT: 'refund reject',
  REFUND_SETTLE: 'refund settle',
  WARDRETURN_ACKNOWLEDGE: 'wardreturn acknowledge',
  WARDRETURN_REJECT: 'wardreturn reject',
};

/**
 * Canonical form of a permission name: lowercase, every run of non-alphanumeric
 * characters collapsed to a single space.
 *
 * The payload mixes conventions — `manage facilities`, `view_item`,
 * `Inventory_dashboard`, `Receipt Cancel Request` — so names are normalised on the
 * way in. This also maps the module's own previous dotted-camel codes onto the
 * current ones (`grn.postToStock` -> `grn posttostock`), so a backend that regressed
 * to the old format would still resolve.
 *
 * INV_ACTIONS values are already canonical, so can() never normalises its argument.
 */
export function normalizePermissionCode(raw) {
  return String(raw || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Module-level entitlements, normalised. These are the same ids the landing page
 * gates its three tiles on (see pages/application-landing/constants.js), so the
 * sidebar and the landing page agree on which workspaces a user has.
 */
export const MODULE_GATES = {
  stock: 'inventory dashboard',
  material: 'material dashboard',
  pharmacy: 'pharmacy dashboard',
};

/**
 * Accepted spellings of the roles that canOverrideGrnPricing() still recognises.
 * Exact match after normalisation, NOT substring — a substring match on "manager"
 * would also admit this tenant's free-text `inventory manager` role.
 */
const ROLE_ALIASES = {
  'store manager': INV_ROLES.STORE_MANAGER,
  admin: INV_ROLES.ADMIN,
  'facility admin': INV_ROLES.ADMIN,
  'super admin': INV_ROLES.ADMIN,
  'inventory manager': INV_ROLES.ADMIN,
};

/**
 * Canonical role key, or null. Only utils/grnEntity.js uses this — see INV_ROLES.
 */
export function normalizeInvRole(raw) {
  const key = String(raw || '').toLowerCase().replace(/[_\s]+/g, ' ').trim();
  return ROLE_ALIASES[key] || null;
}

/** PO lifecycle action → the permission it needs. Unmapped keys are never filtered out. */
const PO_ACTION_PERMISSION = {
  edit: INV_ACTIONS.PO_EDIT,
  delete: INV_ACTIONS.PO_DELETE,
  submit: INV_ACTIONS.PO_SUBMIT,
  approve: INV_ACTIONS.PO_APPROVE,
  reject: INV_ACTIONS.PO_REJECT,
  send_to_vendor: INV_ACTIONS.PO_SEND,
  close: INV_ACTIONS.PO_CLOSE,
  short_close: INV_ACTIONS.PO_SHORT_CLOSE,
  cancel: INV_ACTIONS.PO_CANCEL,
  create_grn: INV_ACTIONS.GRN_CREATE,
};

/** Work Order permissions fall back to RFQ until the backend seeds `workorder *`. */
const WO_FALLBACK = {
  [INV_ACTIONS.WO_VIEW]: INV_ACTIONS.RFQ_VIEW,
  [INV_ACTIONS.WO_CREATE]: INV_ACTIONS.RFQ_CREATE,
  [INV_ACTIONS.WO_EDIT]: INV_ACTIONS.RFQ_EDIT,
  [INV_ACTIONS.WO_PUBLISH]: INV_ACTIONS.RFQ_PUBLISH,
  [INV_ACTIONS.WO_AWARD]: INV_ACTIONS.RFQ_AWARD,
  [INV_ACTIONS.WO_CANCEL]: INV_ACTIONS.RFQ_CANCEL,
  [INV_ACTIONS.WO_EXECUTE]: INV_ACTIONS.RFQ_EDIT,
};

export function canWorkOrder(can, action) {
  if (!can || !action) return false;
  if (can(action)) return true;
  const fallback = WO_FALLBACK[action];
  return Boolean(fallback && can(fallback));
}

/**
 * Narrow the Set from getPoAllowedActions() to what this role may do.
 * Applied at the call sites so poLifecycle.js stays a pure status-lifecycle module.
 * view / print / download / audit are unmapped on purpose — always kept.
 */
export function filterPoActionsByRole(actions, can) {
  const allowed = new Set();
  actions.forEach((action) => {
    const permission = PO_ACTION_PERMISSION[action];
    if (!permission || can(permission)) allowed.add(action);
  });
  return allowed;
}

/**
 * PoDetail and PoDetailDrawer are duplicate surfaces over the same PO actions and
 * share this ConfirmDialog vocabulary, which differs from the lifecycle keys above
 * ('send' vs 'send_to_vendor', 'short-close' vs 'short_close').
 */
export const PO_DIALOG_ACTION_PERMISSION = {
  submit: INV_ACTIONS.PO_SUBMIT,
  approve: INV_ACTIONS.PO_APPROVE,
  reject: INV_ACTIONS.PO_REJECT,
  send: INV_ACTIONS.PO_SEND,
  cancel: INV_ACTIONS.PO_CANCEL,
  close: INV_ACTIONS.PO_CLOSE,
  'short-close': INV_ACTIONS.PO_SHORT_CLOSE,
  delete: INV_ACTIONS.PO_DELETE,
};

const VENDOR_ACTION_PERMISSION = {
  submit: INV_ACTIONS.VENDOR_SUBMIT,
  approve: INV_ACTIONS.VENDOR_APPROVE,
  reject: INV_ACTIONS.VENDOR_REJECT,
  suspend: INV_ACTIONS.VENDOR_SUSPEND,
  blacklist: INV_ACTIONS.VENDOR_BLACKLIST,
  reactivate: INV_ACTIONS.VENDOR_REACTIVATE,
};

/** Narrow the action configs from getAvailableActions() to what this role may do. */
export function filterVendorActionsByRole(actions, can) {
  return actions.filter((action) => {
    const permission = VENDOR_ACTION_PERMISSION[action.key];
    return !permission || can(permission);
  });
}
